import { TUseMainCardsDataProps } from '~/modules/main-page/types.ts';
import { useMemo } from 'react';
import { ClientTypes } from '~/constants/clientTypes.ts';
import {
  mainCardsCounterParty,
  mainCardsLegalResident,
  mainCardsSoleTrader,
} from '~/modules/main-page/Cards/CardsContent';
import { useFeatureFlags } from '~/helpers/FeatureFlagsContext';
import {
  handleRate,
  openInstructionCovenantsPDF,
  openInstructionProcessingPersonalClient,
  openInstructionReIdentificationFopPDF,
  openInstructionReIdentificationLegalResident,
  openInstructionSoleTraderPDF,
} from '~/modules/main-page/utils.ts';
import { useNavigate } from 'react-router';

export const useMainCardsData = (props: TUseMainCardsDataProps) => {
  const {
    activeStep,
    handleCheckUserRegistration,
    handleOpenCovenants,
    handleOpenGuarantees,
    handleUpdatingData,
    handleReidentification,
    canShowGuarantees,
  } = props;

  const {
    isDBOEnabled,
    isGuaranteesEnabled,
    isUpdatingDataFopEnabled,
    isUpdatingDataLegalResidentEnabled,
  } = useFeatureFlags();

  const navigate = useNavigate();

  const handleProcessingPersonalDataForm = () => {
    navigate('/personal-data/application/new');
  };

  const handleRevokeProcessingPersonalDataForm = () => {
    navigate('/personal-data/revoke-application/new');
  };

  const handleRemoteBankingContacts = () => {
    navigate('/remote-banking/contact-information');
  };

  const mainCardsData = useMemo(() => {
    if (activeStep === 'LEGAL_RESIDENT') {
      return mainCardsLegalResident(
        isDBOEnabled,
        handleRemoteBankingContacts,
        handleOpenCovenants,
        handleReidentification,
        openInstructionReIdentificationLegalResident,
        openInstructionCovenantsPDF,
        isGuaranteesEnabled && canShowGuarantees,
        handleOpenGuarantees,
        isUpdatingDataLegalResidentEnabled,
        () => handleUpdatingData(ClientTypes.LEGAL_RESIDENT)
      );
    }

    if (activeStep === 'SOLE_TRADER') {
      return mainCardsSoleTrader(
        isDBOEnabled,
        handleRemoteBankingContacts,
        handleCheckUserRegistration,
        handleRate,
        openInstructionSoleTraderPDF,
        handleReidentification,
        handleOpenCovenants,
        openInstructionReIdentificationFopPDF,
        openInstructionCovenantsPDF,
        isGuaranteesEnabled && canShowGuarantees,
        handleOpenGuarantees,
        isUpdatingDataFopEnabled,
        () => handleUpdatingData(ClientTypes.SOLE_TRADER)
      );
    }

    if (activeStep === 'COUNTERPARTY') {
      return mainCardsCounterParty(
        handleProcessingPersonalDataForm,
        handleRevokeProcessingPersonalDataForm,
        openInstructionProcessingPersonalClient
      );
    }

    return null;
  }, [
    activeStep,
    handleRemoteBankingContacts,
    handleCheckUserRegistration,
    handleRate,
    handleOpenCovenants,
    handleOpenGuarantees,
    handleReidentification,
    openInstructionSoleTraderPDF,
    openInstructionReIdentificationFopPDF,
    openInstructionCovenantsPDF,
    handleProcessingPersonalDataForm,
    handleRevokeProcessingPersonalDataForm,
    openInstructionProcessingPersonalClient,
    handleUpdatingData,
    canShowGuarantees,
    isDBOEnabled,
    isGuaranteesEnabled,
    isUpdatingDataFopEnabled,
    isUpdatingDataLegalResidentEnabled,
  ]);

  return mainCardsData;
};
